import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import InputComponent from '../input';
import { ChevronRightSvg, SearchSvg } from '../../assets/svgs';
import { DropdownOptions, SearchContainer, SearchWrapper, SelectDropdownWrapper } from './select-dropdown-secondary.style';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const defaultOptions = [
    { value: 1, label: 'option 1' },
    { value: 2, label: 'option 2' }
];

const SelectDropdownSecondaryComponent = ({ options = defaultOptions, label, name, value, onChange, disabled, mui }) => {
    const dropdownRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);

    const handleSearch = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        const searchValue = e.target.value;
        setSearch(searchValue);

        if (searchValue) {
            const filtered = options.filter(option =>
                option.label.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(options);
        }
    };

    const handleSearchClear = () => {
        setSearch('');
        setFilteredOptions(options);
    };

    const handleDropdown = () => {
        if (!disabled) {
            setDropdownOpen(!dropdownOpen);
            handleSearchClear();
        }
    };

    const handleOptionClick = (option) => {
        if (!disabled) {
            setSelected(option);
            setDropdownOpen(false);
            handleSearchClear();

            if (onChange) {
                onChange({
                    target: {
                        name,
                        value: option ? option.value : null,
                    },
                });
            }
        }
    };

    useEffect(() => {
        // Always set selected to an option object or null, never undefined
        const found = options?.find((e) => e?.value === value);
        setSelected(found || null);
    }, [value, options]);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    useEffect(() => {
        // close dropdown when click outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            {
                mui ? (
                    <Autocomplete
                        disablePortal
                        options={options}
                        value={selected || null}
                        disabled={disabled}
                        onChange={(event, newValue) => {
                            handleOptionClick(newValue);
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.key || option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {option.flag && (
                                    <img 
                                        src={option.flag} 
                                        alt={option.label} 
                                        style={{ width: '20px', height: '15px', border: '1px solid #ccc' }} 
                                    />
                                )}
                                <span>{option.label}</span>
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} name={name} label={label} variant="filled" />}
                    />
                ) : (
                    <SelectDropdownWrapper ref={dropdownRef}>
                        <InputComponent
                            placeholder="Select"
                            type="text"
                            name={name}
                            value={selected?.label || ''}
                            disabled={disabled}
                            onClick={handleDropdown}
                            readOnly
                        />
                        <ChevronRightSvg className={dropdownOpen ? 'chevron-rotate' : ''} />
                        {
                            dropdownOpen && (
                                <DropdownOptions className="custom-scrollbar box-shadow" $showSearch={options && options?.length > 5}>
                                    {
                                        options && options?.length > 5 &&
                                        <SearchWrapper>
                                            <SearchContainer>
                                                <SearchSvg />
                                                <input
                                                    placeholder="Search"
                                                    type="text"
                                                    name={name}
                                                    value={search}
                                                    onChange={handleSearch}
                                                    autoFocus
                                                />
                                            </SearchContainer>
                                        </SearchWrapper>
                                    }
                                    <ul>
                                        {
                                            filteredOptions && filteredOptions?.length > 0 && [...filteredOptions]?.sort((a, b) => a.label.localeCompare(b.label))?.map((option, index) => (
                                                <li
                                                    key={option.key || `${option.value}-${index}`}
                                                    onClick={() => handleOptionClick(option)}
                                                    className={selected?.value === option?.value ? 'active' : ''}
                                                >
                                                    {option.flag ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <img 
                                                                src={option.flag} 
                                                                alt={option.label} 
                                                                style={{ width: '20px', height: '15px', border: '1px solid #ccc' }} 
                                                            />
                                                            <span>{option.label}</span>
                                                        </div>
                                                    ) : (
                                                        <span>{option.label}</span>
                                                    )}
                                                </li>
                                            ))
                                        }
                                        {
                                            (filteredOptions === undefined || filteredOptions.length < 1) &&
                                            <li>No data found!</li>
                                        }
                                    </ul>
                                </DropdownOptions>
                            )
                        }
                    </SelectDropdownWrapper>
                )
            }
        </>
    );
};

SelectDropdownSecondaryComponent.propTypes = {
    options: PropTypes.array,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    mui: PropTypes.bool,
};

export default SelectDropdownSecondaryComponent;
